import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Card,
  Tab,
  Tabs,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTheme } from '../../../ThemeContext';
import { GooglePlayIcon, AppleIcon } from '../../common/StoreIcons';
import { PLAY_STORE_URL, APP_STORE_URL, EDITOR_PLAY_STORE_URL, AI_PLAY_STORE_URL } from '../../../constants/links';
import { AppQRScannerModal, WhatsAppIcon } from '../../common/AppQRScanner';

// Icons
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import VideoSettingsRoundedIcon from '@mui/icons-material/VideoSettingsRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import ColorLensRoundedIcon from '@mui/icons-material/ColorLensRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';

// Images & Logos
import SS1 from '../../../assets/SS1.png';
import SS2 from '../../../assets/SS2.png';
import SS3 from '../../../assets/SS3.png';
import appLogo from '../../../assets/Filmhook_Apps_Logoonly.png';
import editorIcon from '../../../assets/editor/editor_icon.png';
import editorShot1 from '../../../assets/editor/editor_shot_1.png';
import editorShot2 from '../../../assets/editor/editor_shot_2.png';
import editorShot3 from '../../../assets/editor/editor_shot_3.png';
import editorShot4 from '../../../assets/editor/editor_shot_4.png';
import aiIcon from '../../../assets/filmhook_ai_icon.png';
import aiShot1 from '../../../assets/ai_shot_1.png';
import aiShot2 from '../../../assets/ai_shot_2.png';
import aiShot3 from '../../../assets/ai_shot_3.png';

import FilmhookAIStudioModal from './FilmhookAIStudioModal';
import FilmhookColorLUTSandboxModal from './FilmhookColorLUTSandboxModal';
import FilmhookNLETimelineScrubber from './FilmhookNLETimelineScrubber';
import FilmhookStoryboardGenerator from './FilmhookStoryboardGenerator';
import CinemaTiltCard from './CinemaTiltCard';

// Product 1: Filmhook App Screenshots
const filmhookScreenshots = [
  {
    id: 0,
    title: 'Cinematic Feed & Stories',
    desc: 'Connect with verified filmmakers, directors & crews in real-time',
    tag: 'Main Feed',
    image: SS1,
  },
  {
    id: 1,
    title: 'Talent Showcase & Showreels',
    desc: 'High-res portfolio for actors, models & cinematographers',
    tag: 'Talent Profile',
    image: SS2,
  },
  {
    id: 2,
    title: 'Auditions & Casting Calls',
    desc: 'Browse, filter and apply to film and series auditions with 1 tap',
    tag: 'Casting Calls',
    image: SS3,
  },
];

// Product 2: Filmhook Editor Screenshots
const editorScreenshots = [
  {
    id: 0,
    title: 'Create • Edit • Transform',
    desc: 'Powerful multi-track video timeline & AI tools for creators',
    tag: 'Multi-Track Timeline',
    image: editorShot1,
  },
  {
    id: 1,
    title: 'AI Background Cutout',
    desc: 'Instant 1-tap background eraser with pixel-perfect accuracy',
    tag: 'Auto BG Removal',
    image: editorShot2,
  },
  {
    id: 2,
    title: 'AI Face Enhancer',
    desc: 'Clearer, sharper and more detailed faces with neural AI',
    tag: 'Face Restoration',
    image: editorShot3,
  },
  {
    id: 3,
    title: 'Earn Coins & Credits',
    desc: 'Rewards system to unlock premium tools and high-res exports',
    tag: 'Creator Rewards',
    image: editorShot4,
  },
];

// Product 3: Filmhook AI Screenshots (Official Google Play Store Screenshots)
const aiScreenshots = [
  {
    id: 0,
    title: 'AI Chat Assistant for Cinema',
    desc: 'Ask anything, explore ideas, brainstorm storylines & solve creative filmmaking blocks',
    tag: 'Aether 1.0 Pro Chat',
    image: aiShot1,
  },
  {
    id: 1,
    title: 'Assistant Personalization & Models',
    desc: 'Customize AI experience, notes memory, cinema plugins & neural model selection',
    tag: 'Assistant Setup',
    image: aiShot2,
  },
  {
    id: 2,
    title: 'Filmmaking & Productivity Tools',
    desc: 'Script writer, translate subtitles, summarize PDFs, extract text & generate images',
    tag: 'Script & Production Tools',
    image: aiShot3,
  },
];

/**
 * 3D Trio Smartphone Showcase Stage with Illuminated Futuristic Podium, 
 * Multi-device Perspective, Dynamic Screen Cycling & Floating Motion
 */
const ProductSmartphoneFrame = ({
  appTitle,
  appIconImg,
  ratingText,
  accentColor,
  shots,
  activeShotIdx,
  onSelectShot,
  badgeTop,
  badgeBottom,
  autoSlideInterval = 3500,
}) => {
  const { isDarkMode } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const activeShot = shots[activeShotIdx] || shots[0];
  const leftIdx = (activeShotIdx - 1 + shots.length) % shots.length;
  const rightIdx = (activeShotIdx + 1) % shots.length;
  const leftShot = shots[leftIdx] || shots[0];
  const rightShot = shots[rightIdx] || shots[0];

  const timerRef = useRef(null);

  // Auto-slide progress & cycle effect
  useEffect(() => {
    if (!isPlaying || isHovered) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    const stepMs = 50;
    const increment = (stepMs / autoSlideInterval) * 100;

    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          onSelectShot((curr) => (curr + 1) % shots.length);
          return 0;
        }
        return prev + increment;
      });
    }, stepMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isHovered, autoSlideInterval, shots.length, onSelectShot, activeShotIdx]);

  // Reset progress when slide changes manually
  const handleSelectShot = (idx) => {
    setProgress(0);
    onSelectShot(idx);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setProgress(0);
    onSelectShot((prev) => (prev - 1 + shots.length) % shots.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setProgress(0);
    onSelectShot((prev) => (prev + 1) % shots.length);
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    setIsPlaying((prev) => !prev);
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        width: '100%',
        py: { xs: 2, md: 3 },
      }}
    >
      {/* Background Ambient Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '320px', md: '520px' },
          height: '350px',
          background: `radial-gradient(circle, ${accentColor}35 0%, transparent 70%)`,
          filter: 'blur(70px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Top Floating Rating Badge with Play/Pause Control */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          px: 2.2,
          py: 0.8,
          borderRadius: '20px',
          background: isDarkMode ? 'rgba(15, 23, 42, 0.92)' : '#ffffff',
          border: isDarkMode ? '1.5px solid rgba(255,255,255,0.15)' : '1.5px solid #e2e8f0',
          boxShadow: isDarkMode
            ? `0 10px 30px rgba(0,0,0,0.6), 0 0 20px ${accentColor}25`
            : `0 10px 30px rgba(15, 23, 42, 0.1), 0 0 20px ${accentColor}20`,
          mb: 1.8,
          zIndex: 5,
        }}
      >
        <StarRoundedIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
        <Typography
          variant="caption"
          fontWeight={800}
          sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '0.78rem' }}
        >
          {ratingText}
        </Typography>

        <Box sx={{ width: '1px', height: 16, bgcolor: isDarkMode ? 'rgba(255,255,255,0.2)' : '#e2e8f0', mx: 0.5 }} />

        {/* Auto Slider Play/Pause Indicator */}
        <Tooltip title={isPlaying ? 'Pause Auto Slider' : 'Resume Auto Slider'} arrow>
          <Box
            onClick={togglePlay}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: isPlaying ? accentColor : isDarkMode ? '#94a3b8' : '#64748b',
              fontSize: '0.68rem',
              fontWeight: 700,
              '&:hover': { opacity: 0.8 },
            }}
          >
            {isPlaying ? (
              <PauseRoundedIcon sx={{ fontSize: 15 }} />
            ) : (
              <PlayArrowRoundedIcon sx={{ fontSize: 15 }} />
            )}
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 800 }}>
              {isPlaying ? 'Auto' : 'Paused'}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Switcher Category Chips Above Stage */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.8,
          justifyContent: 'center',
          flexWrap: 'wrap',
          mb: 2.2,
          zIndex: 5,
        }}
      >
        {shots.map((shot, idx) => {
          const isSelected = activeShotIdx === idx;
          return (
            <Chip
              key={shot.id}
              label={shot.tag}
              onClick={() => handleSelectShot(idx)}
              size="small"
              sx={{
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: '0.74rem',
                borderRadius: '12px',
                bgcolor: isSelected
                  ? accentColor
                  : isDarkMode
                  ? 'rgba(255,255,255,0.06)'
                  : '#f1f5f9',
                color: isSelected ? '#ffffff' : isDarkMode ? '#94a3b8' : '#64748b',
                border: isSelected ? `1.5px solid ${accentColor}` : '1.5px solid transparent',
                boxShadow: isSelected ? `0 4px 14px ${accentColor}50` : 'none',
                transition: 'all 0.25s ease',
                '&:hover': {
                  bgcolor: isSelected
                    ? accentColor
                    : isDarkMode
                    ? 'rgba(255,255,255,0.12)'
                    : '#e2e8f0',
                },
              }}
            />
          );
        })}
      </Box>

      {/* ══════════════════════════════════════════════════════════
          3D TRIO PHONE SHOWCASE STAGE WITH ILLUMINATED PODIUM
          ══════════════════════════════════════════════════════════ */}
      <motion.div
        animate={{ y: [0, -9, 0] }}
        transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          zIndex: 2,
        }}
      >
        {/* Stage Wrapper */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: 340, sm: 540, md: 620, lg: 660 },
            height: { xs: 430, sm: 490, md: 540 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          {/* 3D Tiered Illuminated Metallic Podium Platform at Base */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: '98%', sm: '94%', md: '96%' },
              maxWidth: 640,
              height: { xs: 65, md: 85 },
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {/* Ambient Floor Reflection Under Podium */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -15,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '85%',
                height: 40,
                borderRadius: '50%',
                background: `radial-gradient(ellipse, ${accentColor} 0%, transparent 70%)`,
                filter: 'blur(30px)',
                opacity: isDarkMode ? 0.75 : 0.5,
              }}
            />

            {/* Bottom Podium Bevel Tier */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: { xs: 50, md: 65 },
                borderRadius: '50%',
                background: isDarkMode
                  ? 'linear-gradient(180deg, #1e293b 0%, #0b1329 60%, #020617 100%)'
                  : 'linear-gradient(180deg, #f1f5f9 0%, #cbd5e1 60%, #94a3b8 100%)',
                border: isDarkMode ? '1.5px solid rgba(255,255,255,0.15)' : '1.5px solid #cbd5e1',
                boxShadow: isDarkMode
                  ? '0 30px 70px rgba(0,0,0,0.9), inset 0 2px 4px rgba(255,255,255,0.2)'
                  : '0 30px 60px rgba(15,23,42,0.22), inset 0 2px 4px rgba(255,255,255,0.85)',
              }}
            />

            {/* Middle Glowing LED Neon Ring */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 10, md: 14 },
                left: '3.5%',
                right: '3.5%',
                height: { xs: 40, md: 52 },
                borderRadius: '50%',
                border: `2.5px solid ${accentColor}`,
                boxShadow: `0 0 25px ${accentColor}, 0 0 50px ${accentColor}90, inset 0 0 16px ${accentColor}80`,
                background: isDarkMode ? 'rgba(2, 6, 23, 0.5)' : 'rgba(255, 255, 255, 0.45)',
              }}
            />

            {/* Top Reflective Metallic Stage Disc */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 18, md: 24 },
                left: '7%',
                right: '7%',
                height: { xs: 32, md: 42 },
                borderRadius: '50%',
                background: isDarkMode
                  ? 'radial-gradient(ellipse at 50% 25%, rgba(255,255,255,0.25) 0%, rgba(30,41,59,0.95) 50%, rgba(15,23,42,1) 100%)'
                  : 'radial-gradient(ellipse at 50% 25%, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)',
                border: isDarkMode ? '1.2px solid rgba(255,255,255,0.35)' : '1.2px solid #ffffff',
                boxShadow: 'inset 0 1px 4px rgba(255,255,255,0.6)',
              }}
            />
          </Box>

          {/* ══════════════════════════════════════
              PHONE 1: FLANKING LEFT (3D Angle)
              ══════════════════════════════════════ */}
          <Tooltip title={`Tap to center: ${leftShot.tag}`} arrow placement="top">
            <Box
              onClick={() => handleSelectShot(leftIdx)}
              sx={{
                position: 'absolute',
                left: { xs: 0, sm: 8, md: 16 },
                bottom: { xs: 30, sm: 38, md: 45 },
                zIndex: 2,
                cursor: 'pointer',
                width: { xs: 135, sm: 175, md: 200, lg: 215 },
                height: { xs: 275, sm: 350, md: 410, lg: 435 },
                borderRadius: '32px',
                p: '7px',
                background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)',
                border: `2.5px solid ${accentColor}88`,
                transform: 'perspective(1000px) rotateY(18deg) rotateZ(-3deg) scale(0.92)',
                transformOrigin: 'bottom right',
                boxShadow: isDarkMode
                  ? `0 20px 50px rgba(0,0,0,0.8), 0 0 30px ${accentColor}25`
                  : `0 20px 40px rgba(15, 23, 42, 0.18), 0 0 25px ${accentColor}25`,
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'perspective(1000px) rotateY(12deg) rotateZ(-1deg) scale(0.96)',
                  zIndex: 5,
                  borderColor: accentColor,
                  boxShadow: `0 25px 60px rgba(0,0,0,0.9), 0 0 40px ${accentColor}50`,
                },
              }}
            >
              {/* Dynamic Island Notch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 50,
                  height: 12,
                  borderRadius: '0 0 8px 8px',
                  background: '#020617',
                  zIndex: 4,
                }}
              />

              {/* Tag Pill */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 22,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(2, 6, 23, 0.85)',
                  backdropFilter: 'blur(6px)',
                  border: `1px solid ${accentColor}60`,
                  borderRadius: '10px',
                  px: 1,
                  py: 0.3,
                  zIndex: 4,
                  whiteSpace: 'nowrap',
                  maxWidth: '85%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '0.62rem', fontWeight: 800 }}>
                  {leftShot.tag}
                </Typography>
              </Box>

              {/* Screen Image */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  bgcolor: '#000',
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={leftShot.image}
                  alt={leftShot.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    display: 'block',
                    filter: 'brightness(0.92)',
                  }}
                />
                {/* Diagonal Gloss */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%)',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            </Box>
          </Tooltip>

          {/* ══════════════════════════════════════
              PHONE 2: HERO CENTER (Front Facing)
              ══════════════════════════════════════ */}
          <Box
            sx={{
              position: 'relative',
              bottom: { xs: 15, sm: 20, md: 26 },
              zIndex: 4,
              display: 'inline-block',
            }}
          >
            {/* Floating Companion Badge: TOP RIGHT */}
            {badgeTop && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 15,
                  right: { xs: -15, sm: -30, md: -45 },
                  zIndex: 7,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1.2,
                  px: 1.6,
                  py: 0.9,
                  borderRadius: '16px',
                  background: isDarkMode ? 'rgba(5, 10, 24, 0.94)' : 'rgba(255, 255, 255, 0.96)',
                  backdropFilter: 'blur(12px)',
                  border: `1.5px solid ${accentColor}55`,
                  boxShadow: `0 12px 30px rgba(0,0,0,0.35), 0 0 15px ${accentColor}25`,
                  animation: 'floatBadge 4s ease-in-out infinite',
                  '@keyframes floatBadge': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-5px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9px',
                    bgcolor: `${accentColor}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accentColor,
                  }}
                >
                  {badgeTop.icon}
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontWeight: 800, fontSize: '0.76rem' }}>
                    {badgeTop.title}
                  </Typography>
                  <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: '0.66rem' }}>
                    {badgeTop.sub}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Floating Companion Badge: BOTTOM LEFT */}
            {badgeBottom && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 80,
                  left: { xs: -15, sm: -35, md: -50 },
                  zIndex: 7,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1.2,
                  px: 1.6,
                  py: 0.9,
                  borderRadius: '16px',
                  background: isDarkMode ? 'rgba(5, 10, 24, 0.94)' : 'rgba(255, 255, 255, 0.96)',
                  backdropFilter: 'blur(12px)',
                  border: `1.5px solid ${accentColor}55`,
                  boxShadow: `0 12px 30px rgba(0,0,0,0.35), 0 0 15px ${accentColor}25`,
                  animation: 'floatBadgeRev 4.5s ease-in-out infinite',
                  '@keyframes floatBadgeRev': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(5px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9px',
                    bgcolor: `${accentColor}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: accentColor,
                  }}
                >
                  {badgeBottom.icon}
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: isDarkMode ? '#ffffff' : '#0f172a', fontWeight: 800, fontSize: '0.76rem' }}>
                    {badgeBottom.title}
                  </Typography>
                  <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.66rem' }}>
                    {badgeBottom.sub}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* 3D Realistic Center Smartphone Frame */}
            <Box
              sx={{
                width: { xs: 215, sm: 245, md: 275, lg: 290 },
                height: { xs: 415, sm: 465, md: 515, lg: 535 },
                borderRadius: '40px',
                p: '9px',
                background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 50%, #020617 100%)',
                boxShadow: isDarkMode
                  ? `0 28px 70px rgba(0,0,0,0.85), 0 0 50px ${accentColor}35, inset 0 0 4px rgba(255,255,255,0.2)`
                  : `0 28px 70px rgba(15, 23, 42, 0.2), 0 0 40px ${accentColor}35, inset 0 0 4px rgba(255,255,255,0.4)`,
                border: `3.5px solid ${accentColor}`,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: isDarkMode
                    ? `0 35px 85px rgba(0,0,0,0.9), 0 0 65px ${accentColor}50`
                    : `0 35px 85px rgba(15, 23, 42, 0.28), 0 0 55px ${accentColor}50`,
                },
              }}
            >
              {/* Dynamic Island Notch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 9,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 82,
                  height: 18,
                  borderRadius: '0 0 13px 13px',
                  background: '#020617',
                  zIndex: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                }}
              >
                <Box sx={{ width: 38, height: 3.5, borderRadius: 2, bgcolor: '#1e293b' }} />
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#09152e',
                    border: '1px solid #1e293b',
                    ml: 1,
                  }}
                />
              </Box>

              {/* Diagonal Glass Reflection Highlight */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '110px',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 40%, transparent 60%)',
                  zIndex: 3,
                  pointerEvents: 'none',
                  borderRadius: '32px',
                }}
              />

              {/* Screenshot Image Viewport */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  borderRadius: '31px',
                  overflow: 'hidden',
                  bgcolor: '#000000',
                }}
              >
                {/* Story-Style Dynamic Segment Progress Bars Under Notch */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '88%',
                    display: 'flex',
                    gap: 0.6,
                    zIndex: 6,
                  }}
                >
                  {shots.map((_, i) => {
                    let fillPercent = 0;
                    if (i < activeShotIdx) fillPercent = 100;
                    else if (i === activeShotIdx) fillPercent = progress;
                    else fillPercent = 0;

                    return (
                      <Box
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectShot(i);
                        }}
                        sx={{
                          flex: 1,
                          height: '3.5px',
                          borderRadius: '3px',
                          bgcolor: 'rgba(255, 255, 255, 0.28)',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          backdropFilter: 'blur(4px)',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${fillPercent}%`,
                            height: '100%',
                            bgcolor: accentColor,
                            boxShadow: `0 0 6px ${accentColor}`,
                            transition: i === activeShotIdx ? 'none' : 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>

                {/* Left Chevron Button */}
                <IconButton
                  size="small"
                  onClick={handlePrev}
                  sx={{
                    position: 'absolute',
                    left: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 6,
                    color: '#ffffff',
                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                    backdropFilter: 'blur(6px)',
                    p: 0.5,
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: accentColor, color: '#fff' },
                  }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Right Chevron Button */}
                <IconButton
                  size="small"
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 6,
                    color: '#ffffff',
                    bgcolor: 'rgba(0, 0, 0, 0.45)',
                    backdropFilter: 'blur(6px)',
                    p: 0.5,
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: accentColor, color: '#fff' },
                  }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Screen Image with Crossfade */}
                <Box
                  component="img"
                  src={activeShot.image}
                  alt={activeShot.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    display: 'block',
                    transition: 'opacity 0.35s ease, transform 0.4s ease',
                  }}
                />

                {/* Bottom Floating Verified Pill */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '92%',
                    p: 1,
                    borderRadius: '15px',
                    background: 'rgba(2, 6, 23, 0.92)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: `1.5px solid ${accentColor}70`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.1,
                    zIndex: 5,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.7)',
                  }}
                >
                  <Box
                    component="img"
                    src={appIconImg}
                    alt={appTitle}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '8px',
                      objectFit: 'cover',
                      border: `1px solid ${accentColor}50`,
                    }}
                  />
                  <Box sx={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#ffffff',
                        fontSize: '0.74rem',
                        fontWeight: 900,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {appTitle}
                    </Typography>
                    <Typography
                      sx={{
                        color: accentColor,
                        fontSize: '0.64rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {activeShot.title}
                    </Typography>
                  </Box>
                  <VerifiedRoundedIcon sx={{ fontSize: 16, color: '#10b981', flexShrink: 0 }} />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ══════════════════════════════════════
              PHONE 3: FLANKING RIGHT (3D Angle)
              ══════════════════════════════════════ */}
          <Tooltip title={`Tap to center: ${rightShot.tag}`} arrow placement="top">
            <Box
              onClick={() => handleSelectShot(rightIdx)}
              sx={{
                position: 'absolute',
                right: { xs: 0, sm: 8, md: 16 },
                bottom: { xs: 30, sm: 38, md: 45 },
                zIndex: 2,
                cursor: 'pointer',
                width: { xs: 135, sm: 175, md: 200, lg: 215 },
                height: { xs: 275, sm: 350, md: 410, lg: 435 },
                borderRadius: '32px',
                p: '7px',
                background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)',
                border: `2.5px solid ${accentColor}88`,
                transform: 'perspective(1000px) rotateY(-18deg) rotateZ(3deg) scale(0.92)',
                transformOrigin: 'bottom left',
                boxShadow: isDarkMode
                  ? `0 20px 50px rgba(0,0,0,0.8), 0 0 30px ${accentColor}25`
                  : `0 20px 40px rgba(15, 23, 42, 0.18), 0 0 25px ${accentColor}25`,
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'perspective(1000px) rotateY(-12deg) rotateZ(1deg) scale(0.96)',
                  zIndex: 5,
                  borderColor: accentColor,
                  boxShadow: `0 25px 60px rgba(0,0,0,0.9), 0 0 40px ${accentColor}50`,
                },
              }}
            >
              {/* Dynamic Island Notch */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 50,
                  height: 12,
                  borderRadius: '0 0 8px 8px',
                  background: '#020617',
                  zIndex: 4,
                }}
              />

              {/* Tag Pill */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 22,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(2, 6, 23, 0.85)',
                  backdropFilter: 'blur(6px)',
                  border: `1px solid ${accentColor}60`,
                  borderRadius: '10px',
                  px: 1,
                  py: 0.3,
                  zIndex: 4,
                  whiteSpace: 'nowrap',
                  maxWidth: '85%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Typography sx={{ color: '#fff', fontSize: '0.62rem', fontWeight: 800 }}>
                  {rightShot.tag}
                </Typography>
              </Box>

              {/* Screen Image */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '25px',
                  overflow: 'hidden',
                  bgcolor: '#000',
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={rightShot.image}
                  alt={rightShot.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top',
                    display: 'block',
                    filter: 'brightness(0.92)',
                  }}
                />
                {/* Diagonal Gloss */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%)',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </motion.div>

      {/* Bottom Thumbnail Strip with Glow */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.2,
          justifyContent: 'center',
          mt: 2.2,
          zIndex: 3,
        }}
      >
        {shots.map((shot, idx) => {
          const isSelected = activeShotIdx === idx;
          return (
            <Tooltip key={shot.id} title={`${shot.tag} - Tap to center`} arrow placement="bottom">
              <Box
                onClick={() => handleSelectShot(idx)}
                sx={{
                  width: 48,
                  height: 64,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: isSelected ? `2.5px solid ${accentColor}` : '1.5px solid rgba(255,255,255,0.15)',
                  boxShadow: isSelected ? `0 4px 14px ${accentColor}60` : 'none',
                  opacity: isSelected ? 1 : 0.65,
                  transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                  transition: 'all 0.22s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.1)',
                    borderColor: accentColor,
                  },
                }}
              >
                <Box
                  component="img"
                  src={shot.image}
                  alt={shot.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: isDarkMode ? '#94a3b8' : '#64748b',
          fontSize: '0.72rem',
          mt: 1.2,
          fontWeight: 600,
          textAlign: 'center',
          zIndex: 3,
        }}
      >
        Slide {activeShotIdx + 1} of {shots.length} • Auto-sliding (Hover to pause • Click flanking phones to rotate)
      </Typography>
    </Box>
  );
};

const ProductShowcaseSection = ({
  onOpenWaitlist,
  activeSectionRef,
  selectedTab: controlledTab,
  onTabChange,
}) => {
  const { isDarkMode } = useTheme();
  const [internalTab, setInternalTab] = useState(0);
  const selectedTab = controlledTab !== undefined ? controlledTab : internalTab;
  const [aiStudioOpen, setAiStudioOpen] = useState(false);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [filmhookActiveShot, setFilmhookActiveShot] = useState(0);
  const [editorActiveShot, setEditorActiveShot] = useState(0);
  const [aiActiveShot, setAiActiveShot] = useState(0);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrTargetId, setQrTargetId] = useState('filmhook-android');
  const [qrInitialTab, setQrInitialTab] = useState(0);

  const handleOpenQR = (targetId, initialTab = 0) => {
    setQrTargetId(targetId || 'filmhook-android');
    setQrInitialTab(initialTab);
    setQrModalOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    if (onTabChange) {
      onTabChange(newValue);
    } else {
      setInternalTab(newValue);
    }
  };

  return (
    <Box
      id="products-showcase"
      ref={activeSectionRef}
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: isDarkMode ? '#050a14' : '#fafbfe',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* High-Level Cinema Atmospheric Floating Aurora Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '5%',
          left: '2%',
          width: { xs: 260, md: 480 },
          height: { xs: 260, md: 480 },
          borderRadius: '50%',
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(7, 164, 245, 0.14) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(7, 164, 245, 0.08) 0%, transparent 70%)',
          filter: 'blur(50px)',
          pointerEvents: 'none',
          zIndex: 0,
          animation: 'floatSlow 12s ease-in-out infinite',
          '@keyframes floatSlow': {
            '0%, 100%': { transform: 'translateY(0px) scale(1)' },
            '50%': { transform: 'translateY(-30px) scale(1.08)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '35%',
          right: '2%',
          width: { xs: 280, md: 520 },
          height: { xs: 280, md: 520 },
          borderRadius: '50%',
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(139, 92, 246, 0.07) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
          zIndex: 0,
          animation: 'floatSlow 15s ease-in-out infinite reverse',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          left: '10%',
          width: { xs: 240, md: 440 },
          height: { xs: 240, md: 440 },
          borderRadius: '50%',
          background: isDarkMode
            ? 'radial-gradient(circle, rgba(6, 182, 212, 0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(6, 182, 212, 0.06) 0%, transparent 70%)',
          filter: 'blur(55px)',
          pointerEvents: 'none',
          zIndex: 0,
          animation: 'floatSlow 10s ease-in-out infinite 2s',
        }}
      />
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
          <Chip
            label="Flagship Offerings"
            sx={{
              fontWeight: 800,
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#07a4f5',
              backgroundColor: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
              border: '1px solid rgba(7, 164, 245, 0.25)',
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
              fontWeight: 900,
              color: isDarkMode ? '#ffffff' : '#0d1530',
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Explore The Product Lineup
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              maxWidth: 680,
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            From on-set talent discovery to timeline post-production and generative AI, each product is built from the ground up for cinema creators.
          </Typography>

          {/* Interactive Navigation Tabs */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                background: isDarkMode ? 'rgba(15, 23, 42, 0.6)' : '#f1f5f9',
                p: 0.6,
                borderRadius: '24px',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              {[
                { label: 'Filmhook Apps', icon: <PhoneIphoneRoundedIcon sx={{ fontSize: 18 }} /> },
                { label: 'Filmhook Editor', icon: <VideoSettingsRoundedIcon sx={{ fontSize: 18 }} /> },
                { label: 'Filmhook AI', icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} /> },
              ].map((tab, idx) => (
                <Tab
                  key={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  sx={{
                    borderRadius: '20px',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    textTransform: 'none',
                    minHeight: 44,
                    px: { xs: 2, sm: 3 },
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    transition: 'all 0.25s ease',
                    ...(selectedTab === idx && {
                      color: '#ffffff !important',
                      background:
                        idx === 0
                          ? 'linear-gradient(90deg, #07a4f5 0%, #38bdf8 100%)'
                          : idx === 1
                          ? 'linear-gradient(90deg, #8b5cf6 0%, #a855f7 100%)'
                          : 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                      boxShadow:
                        idx === 0
                          ? '0 6px 20px rgba(7, 164, 245, 0.35)'
                          : idx === 1
                          ? '0 6px 20px rgba(139, 92, 246, 0.35)'
                          : '0 6px 20px rgba(6, 182, 212, 0.35)',
                    }),
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            PRODUCT 1: FILMHOOK APPS (Tab 0)
            ══════════════════════════════════════════════════════════ */}
        <AnimatePresence mode="wait">
          {selectedTab === 0 && (
            <Box
              component={motion.div}
              key="tab-filmhook-apps"
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
              transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            >
              <CinemaTiltCard accentColor="#07a4f5" isDarkMode={isDarkMode}>
                <Card
            sx={{
              borderRadius: '28px',
              p: { xs: 3, md: 6 },
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(13,27,51,0.85) 0%, rgba(5,10,20,0.95) 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
              border: isDarkMode ? '1.5px solid rgba(7, 164, 245, 0.3)' : '1.5px solid rgba(7, 164, 245, 0.2)',
              boxShadow: isDarkMode
                ? '0 24px 64px rgba(0,0,0,0.6)'
                : '0 24px 64px rgba(7, 164, 245, 0.12)',
            }}
          >
            <Grid container spacing={{ xs: 4, lg: 5 }} alignItems="center">
              {/* Left Column: Details & Feature Highlights */}
              <Grid item xs={12} lg={5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Chip
                    label="LIVE ON IOS & ANDROID"
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      letterSpacing: '0.06em',
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#10b981',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                    }}
                  />
                  <Chip
                    label="v1.0 Available Now"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
                      color: isDarkMode ? '#94a3b8' : '#475569',
                    }}
                  />
                </Box>

                <Typography
                  variant="h3"
                  fontWeight={900}
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#0d1530',
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    lineHeight: 1.2,
                    mb: 2,
                  }}
                >
                  Filmhook Apps
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#07a4f5',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    mb: 2,
                  }}
                >
                  The Definitive Cinematic Social Network, Auditions & Talent Directory
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontSize: '0.96rem',
                    lineHeight: 1.75,
                    mb: 4,
                  }}
                >
                  Filmhook Apps unites the entire entertainment industry in a single mobile experience. Apply to casting calls with one tap, showcase your video portfolio and showreels, discover verified shooting locations, broadcast behind-the-scenes live streams, and chat directly with directors.
                </Typography>

                {/* Bullet Points */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
                  {[
                    { icon: <MovieCreationRoundedIcon sx={{ color: '#07a4f5' }} />, text: 'Verified Casting Calls & Auditions Tracking' },
                    { icon: <PersonSearchRoundedIcon sx={{ color: '#07a4f5' }} />, text: 'Actor, Model & Crew Talent Portfolios' },
                    { icon: <CampaignRoundedIcon sx={{ color: '#07a4f5' }} />, text: 'Shooting Locations Booking Directory' },
                    { icon: <GroupsRoundedIcon sx={{ color: '#07a4f5' }} />, text: 'Live Industry Broadcasts & Real-time Chat' },
                  ].map((feat, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          bgcolor: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {feat.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: '0.85rem', pt: 0.5 }}
                      >
                        {feat.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Download Store Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    sx={{
                      background: 'linear-gradient(90deg, #07a4f5 0%, #38bdf8 100%)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 3,
                      py: 1.2,
                      textTransform: 'none',
                      boxShadow: '0 8px 20px rgba(7, 164, 245, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #068bc2 0%, #0284c7 100%)',
                        boxShadow: '0 12px 28px rgba(7, 164, 245, 0.55)',
                      },
                    }}
                  >
                    Google Play Store
                  </Button>

                  <Button
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<AppleIcon size={20} />}
                    sx={{
                      background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#ffffff',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid #cbd5e1',
                      color: isDarkMode ? '#ffffff' : '#0d1530',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 3,
                      py: 1.2,
                      textTransform: 'none',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                      '&:hover': {
                        background: isDarkMode ? 'rgba(255,255,255,0.15)' : '#f8fafc',
                      },
                    }}
                  >
                    App Store (iOS)
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-android', 0)}
                    startIcon={<QrCodeScannerRoundedIcon />}
                    sx={{
                      background: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : '#e0f2fe',
                      color: '#07a4f5',
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      border: '1.5px solid rgba(7, 164, 245, 0.35)',
                      '&:hover': {
                        background: '#07a4f5',
                        color: '#fff',
                      },
                    }}
                  >
                    Scan QR
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-android', 1)}
                    startIcon={<WhatsAppIcon size={17} />}
                    sx={{
                      background: isDarkMode ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.08)',
                      color: isDarkMode ? '#4ade80' : '#15803d',
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      border: '1.5px solid rgba(37, 211, 102, 0.35)',
                      boxShadow: '0 4px 14px rgba(37, 211, 102, 0.15)',
                      '&:hover': {
                        background: '#25D366',
                        color: '#fff',
                        borderColor: '#25D366',
                        boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)',
                      },
                    }}
                  >
                    Send to Phone
                  </Button>
                </Box>
              </Grid>

              {/* Right Column: Premium 3D Smartphone Trio Stage with Podium */}
              <Grid item xs={12} lg={7}>
                <ProductSmartphoneFrame
                  appTitle="Filmhook Apps"
                  appIconImg={appLogo}
                  ratingText="4.8 Rating • 100K+ Film Community"
                  accentColor="#07a4f5"
                  shots={filmhookScreenshots}
                  activeShotIdx={filmhookActiveShot}
                  onSelectShot={setFilmhookActiveShot}
                  badgeTop={{
                    icon: <MovieCreationRoundedIcon sx={{ fontSize: 17 }} />,
                    title: 'Audition Call Verified',
                    sub: 'Lead Role • Apply in 1 Tap',
                  }}
                  badgeBottom={{
                    icon: <CampaignRoundedIcon sx={{ fontSize: 17 }} />,
                    title: '4K Location Scouted',
                    sub: 'Verified Shooting Space',
                  }}
                />
              </Grid>
            </Grid>
                </Card>
              </CinemaTiltCard>
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              PRODUCT 2: FILMHOOK EDITOR (Tab 1)
              ══════════════════════════════════════════════════════════ */}
          {selectedTab === 1 && (
            <Box
              component={motion.div}
              key="tab-filmhook-editor"
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
              transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            >
              <CinemaTiltCard accentColor="#8b5cf6" isDarkMode={isDarkMode}>
                <Card
            sx={{
              borderRadius: '28px',
              p: { xs: 3, md: 6 },
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(29,15,48,0.85) 0%, rgba(10,5,20,0.95) 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
              border: isDarkMode ? '1.5px solid rgba(139, 92, 246, 0.35)' : '1.5px solid rgba(139, 92, 246, 0.25)',
              boxShadow: isDarkMode
                ? '0 24px 64px rgba(0,0,0,0.6)'
                : '0 24px 64px rgba(139, 92, 246, 0.12)',
            }}
          >
            <Grid container spacing={{ xs: 4, lg: 5 }} alignItems="center">
              {/* Left Column: Details & Feature Highlights */}
              <Grid item xs={12} lg={5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<GooglePlayIcon size={14} />}
                    label="NOW ON GOOGLE PLAY"
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      letterSpacing: '0.06em',
                      bgcolor: 'rgba(139, 92, 246, 0.2)',
                      color: '#c084fc',
                      border: '1px solid rgba(139, 92, 246, 0.45)',
                      px: 0.5,
                    }}
                  />
                  <Chip
                    icon={<StarRoundedIcon sx={{ fontSize: '15px !important', color: '#f59e0b !important' }} />}
                    label="4.8 ★ RATED 12+"
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      bgcolor: 'rgba(245, 158, 11, 0.12)',
                      color: '#f59e0b',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                    }}
                  />
                  <Chip
                    label="All-in-One AI Suite"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f3e8ff',
                      color: isDarkMode ? '#cbd5e1' : '#6b21a8',
                    }}
                  />
                </Box>

                {/* App Brand Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    component="img"
                    src={editorIcon}
                    alt="Filmhook Editor Icon"
                    sx={{
                      width: { xs: 52, sm: 64 },
                      height: { xs: 52, sm: 64 },
                      borderRadius: '16px',
                      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.45)',
                      border: '1.5px solid rgba(139, 92, 246, 0.4)',
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: isDarkMode ? '#ffffff' : '#0d1530',
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                        lineHeight: 1.15,
                      }}
                    >
                      Filmhook Editor
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#a855f7',
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '0.98rem' },
                        mt: 0.3,
                      }}
                    >
                      Create • Edit • Transform — All-in-One AI Video & Photo Studio
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontSize: '0.95rem',
                    lineHeight: 1.75,
                    mb: 3.5,
                  }}
                >
                  The ultimate cinematic video editor and AI photo enhancer designed for creators, filmmakers, and vloggers. Craft viral Reels, YouTube Shorts, and cinema sequences with multi-track magnetic timelines, pixel-perfect AI background removal, and neural face enhancement.
                </Typography>

                {/* Bullet Points from Play Store */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
                  {[
                    {
                      icon: <TimelineRoundedIcon sx={{ color: '#a855f7' }} />,
                      title: 'Multi-Track Timeline Editor',
                      desc: 'Smooth cuts, speed ramps, sound effects & layers for Reels',
                    },
                    {
                      icon: <ColorLensRoundedIcon sx={{ color: '#a855f7' }} />,
                      title: 'AI Background Cutout',
                      desc: '1-tap high accuracy background eraser & custom backdrops',
                    },
                    {
                      icon: <AutoAwesomeRoundedIcon sx={{ color: '#a855f7' }} />,
                      title: 'AI Face Enhancer',
                      desc: 'Restore facial clarity, sharpen eyes & smooth skin textures',
                    },
                    {
                      icon: <StarRoundedIcon sx={{ color: '#a855f7' }} />,
                      title: 'Creator Rewards & Coins',
                      desc: 'Earn export credits, unlock premium LUTs & zero watermark',
                    },
                  ].map((feat, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '10px',
                          bgcolor: 'rgba(139, 92, 246, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {feat.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={800}
                          sx={{ color: isDarkMode ? '#f1f5f9' : '#1e293b', fontSize: '0.86rem' }}
                        >
                          {feat.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.74rem', lineHeight: 1.4, display: 'block' }}
                        >
                          {feat.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    href={EDITOR_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      background: 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.94rem',
                      borderRadius: '25px',
                      px: 3.5,
                      py: 1.3,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(139, 92, 246, 0.45)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #7c3aed 0%, #db2777 100%)',
                        boxShadow: '0 12px 30px rgba(139, 92, 246, 0.6)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Install Filmhook Editor (Play Store)
                  </Button>

                  <Button
                    onClick={() => setColorModalOpen(true)}
                    startIcon={<ColorLensRoundedIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 50%, #8b5cf6 100%)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.92rem',
                      borderRadius: '25px',
                      px: 3,
                      py: 1.25,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(236, 72, 153, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #d97706 0%, #db2777 50%, #7c3aed 100%)',
                        boxShadow: '0 12px 30px rgba(236, 72, 153, 0.6)',
                        transform: 'translateY(-2px) scale(1.02)',
                      },
                    }}
                  >
                    🎨 3D LUT Color Sandbox
                  </Button>

                  <Button
                    onClick={() => onOpenWaitlist && onOpenWaitlist('Filmhook Editor')}
                    sx={{
                      border: isDarkMode ? '1.5px solid rgba(139, 92, 246, 0.4)' : '1.5px solid #8b5cf6',
                      color: isDarkMode ? '#c084fc' : '#7c3aed',
                      background: isDarkMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.05)',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 3,
                      py: 1.2,
                      textTransform: 'none',
                      '&:hover': {
                        background: isDarkMode ? 'rgba(139, 92, 246, 0.18)' : '#faf5ff',
                      },
                    }}
                  >
                    Web Cloud NLE Beta
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-editor', 0)}
                    startIcon={<QrCodeScannerRoundedIcon />}
                    sx={{
                      border: isDarkMode ? '1.5px solid rgba(139, 92, 246, 0.4)' : '1.5px solid #8b5cf6',
                      color: isDarkMode ? '#c084fc' : '#7c3aed',
                      background: isDarkMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.08)',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#8b5cf6',
                        color: '#fff',
                      },
                    }}
                  >
                    Scan QR
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-editor', 1)}
                    startIcon={<WhatsAppIcon size={17} />}
                    sx={{
                      background: isDarkMode ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.08)',
                      color: isDarkMode ? '#4ade80' : '#15803d',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      border: '1.5px solid rgba(37, 211, 102, 0.35)',
                      boxShadow: '0 4px 14px rgba(37, 211, 102, 0.15)',
                      '&:hover': {
                        background: '#25D366',
                        color: '#fff',
                        borderColor: '#25D366',
                        boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)',
                      },
                    }}
                  >
                    Send to Phone
                  </Button>
                </Box>
              </Grid>

              {/* Right Column: Premium 3D Smartphone Trio Stage with Podium */}
              <Grid item xs={12} lg={7}>
                <ProductSmartphoneFrame
                  appTitle="Filmhook Editor"
                  appIconImg={editorIcon}
                  ratingText="4.8 ★ Rated 12+ • All-in-One Studio"
                  accentColor="#a855f7"
                  shots={editorScreenshots}
                  activeShotIdx={editorActiveShot}
                  onSelectShot={setEditorActiveShot}
                  badgeTop={{
                    icon: <ColorLensRoundedIcon sx={{ fontSize: 17 }} />,
                    title: '1-Tap AI Cutout',
                    sub: '99.8% Edge Precision',
                  }}
                  badgeBottom={{
                    icon: <TimelineRoundedIcon sx={{ fontSize: 17 }} />,
                    title: '60 FPS 4K Timeline',
                    sub: 'Zero Watermark Export',
                  }}
                />
              </Grid>
            </Grid>

            {/* Interactive NLE Multi-Track Timeline Scrubber Animation */}
            <FilmhookNLETimelineScrubber
              activeShotIdx={editorActiveShot}
              onScrub={(pct) => {
                const shotIdx = Math.min(3, Math.floor(pct * 4));
                if (shotIdx !== editorActiveShot) {
                  setEditorActiveShot(shotIdx);
                }
              }}
            />
                </Card>
              </CinemaTiltCard>
            </Box>
          )}

          {/* ══════════════════════════════════════════════════════════
              PRODUCT 3: FILMHOOK AI (Tab 2)
              ══════════════════════════════════════════════════════════ */}
          {selectedTab === 2 && (
            <Box
              component={motion.div}
              key="tab-filmhook-ai"
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
              transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
            >
              <CinemaTiltCard accentColor="#06b6d4" isDarkMode={isDarkMode}>
                <Card
            sx={{
              borderRadius: '28px',
              p: { xs: 3, md: 6 },
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(8,27,45,0.85) 0%, rgba(3,10,22,0.95) 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #ecfeff 100%)',
              border: isDarkMode ? '1.5px solid rgba(6, 182, 212, 0.3)' : '1.5px solid rgba(6, 182, 212, 0.2)',
              boxShadow: isDarkMode
                ? '0 24px 64px rgba(0,0,0,0.6)'
                : '0 24px 64px rgba(6, 182, 212, 0.12)',
            }}
          >
            <Grid container spacing={{ xs: 4, lg: 5 }} alignItems="center">
              {/* Left Column: Details & Feature Highlights */}
              <Grid item xs={12} lg={5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Chip
                    label="NOW LIVE ON GOOGLE PLAY"
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      letterSpacing: '0.06em',
                      bgcolor: 'rgba(6, 182, 212, 0.15)',
                      color: '#06b6d4',
                      border: '1px solid rgba(6, 182, 212, 0.35)',
                    }}
                  />
                  <Chip
                    label="Generative Cinema Suite"
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#cffafe',
                      color: isDarkMode ? '#cbd5e1' : '#0e7490',
                    }}
                  />
                </Box>

                {/* App Brand Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    component="img"
                    src={aiIcon}
                    alt="Filmhook AI Icon"
                    sx={{
                      width: { xs: 52, sm: 64 },
                      height: { xs: 52, sm: 64 },
                      borderRadius: '16px',
                      boxShadow: '0 8px 24px rgba(6, 182, 212, 0.45)',
                      border: '1.5px solid rgba(6, 182, 212, 0.4)',
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={900}
                      sx={{
                        color: isDarkMode ? '#ffffff' : '#0d1530',
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                        lineHeight: 1.15,
                      }}
                    >
                      Filmhook AI
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#06b6d4',
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '0.98rem' },
                        mt: 0.3,
                      }}
                    >
                      Generative Intelligence Built Specifically for Scripts, Storyboards & Casting
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontSize: '0.96rem',
                    lineHeight: 1.75,
                    mb: 4,
                  }}
                >
                  Accelerate pre-production and creative ideation. Filmhook AI analyzes script beats, generates visual storyboards from text descriptions, suggests optimal talent matches based on character bios, and previews dialogue with multi-lingual emotional voice dubbing.
                </Typography>

                {/* Bullet Points */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
                  {[
                    { icon: <PsychologyRoundedIcon sx={{ color: '#06b6d4' }} />, text: 'Intelligent Screenplay & Beat Sheet Writing' },
                    { icon: <AutoFixHighRoundedIcon sx={{ color: '#06b6d4' }} />, text: 'Text-to-Cinematic Storyboard Generator' },
                    { icon: <PersonSearchRoundedIcon sx={{ color: '#06b6d4' }} />, text: 'AI Casting Matcher & Role Compatibility' },
                    { icon: <MicRoundedIcon sx={{ color: '#06b6d4' }} />, text: 'Multi-Lingual Character Dialogue Prototyping' },
                  ].map((feat, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '8px',
                          bgcolor: 'rgba(6, 182, 212, 0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {feat.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: '0.85rem', pt: 0.5 }}
                      >
                        {feat.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    component="a"
                    href={AI_PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<GooglePlayIcon size={20} />}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      background: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 100%)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.95rem',
                      borderRadius: '25px',
                      px: 3.5,
                      py: 1.3,
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(6, 182, 212, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #0891b2 0%, #2563eb 100%)',
                        boxShadow: '0 12px 30px rgba(6, 182, 212, 0.6)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Install Filmhook AI (Play Store)
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-ai', 0)}
                    startIcon={<QrCodeScannerRoundedIcon />}
                    sx={{
                      border: isDarkMode ? '1.5px solid rgba(6, 182, 212, 0.4)' : '1.5px solid #06b6d4',
                      color: isDarkMode ? '#38bdf8' : '#0284c7',
                      background: isDarkMode ? 'rgba(6, 182, 212, 0.12)' : 'rgba(6, 182, 212, 0.08)',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      '&:hover': {
                        background: '#06b6d4',
                        color: '#fff',
                      },
                    }}
                  >
                    Scan QR
                  </Button>

                  <Button
                    onClick={() => handleOpenQR('filmhook-ai', 1)}
                    startIcon={<WhatsAppIcon size={17} />}
                    sx={{
                      background: isDarkMode ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.08)',
                      color: isDarkMode ? '#4ade80' : '#15803d',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      borderRadius: '25px',
                      px: 2.5,
                      py: 1.2,
                      textTransform: 'none',
                      border: '1.5px solid rgba(37, 211, 102, 0.35)',
                      boxShadow: '0 4px 14px rgba(37, 211, 102, 0.15)',
                      '&:hover': {
                        background: '#25D366',
                        color: '#fff',
                        borderColor: '#25D366',
                        boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)',
                      },
                    }}
                  >
                    Send to Phone
                  </Button>

                  <Button
                    onClick={() => setAiStudioOpen(true)}
                    startIcon={<AutoAwesomeRoundedIcon />}
                    sx={{
                      background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#0f172a',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '0.92rem',
                      borderRadius: '25px',
                      px: 3,
                      py: 1.2,
                      textTransform: 'none',
                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                      '&:hover': {
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : '#1e293b',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Live AI Studio Demo
                  </Button>
                </Box>
              </Grid>

              {/* Right Column: Premium 3D Smartphone Trio Stage with Podium */}
              <Grid item xs={12} lg={7}>
                <ProductSmartphoneFrame
                  appTitle="Filmhook AI"
                  appIconImg={aiIcon}
                  ratingText="4.9 ★ Rating • Generative Cinema Engine"
                  accentColor="#06b6d4"
                  shots={aiScreenshots}
                  activeShotIdx={aiActiveShot}
                  onSelectShot={setAiActiveShot}
                  badgeTop={{
                    icon: <PsychologyRoundedIcon sx={{ fontSize: 17 }} />,
                    title: 'Script Writer & AI Tools',
                    sub: '8+ Cinema Productivity Engines',
                  }}
                  badgeBottom={{
                    icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />,
                    title: 'Aether 1.0 Pro Engine',
                    sub: 'Multimodal Cinema Intelligence',
                  }}
                />
              </Grid>
            </Grid>

            {/* Interactive Script-to-Storyboard Diffusion Generator Animation */}
            <FilmhookStoryboardGenerator
              activeShotIdx={aiActiveShot}
              onSelectShot={(idx) => setAiActiveShot(idx)}
            />
                </Card>
              </CinemaTiltCard>
            </Box>
          )}
        </AnimatePresence>
      </Container>

      {/* Filmhook AI Interactive Studio Modal */}
      <FilmhookAIStudioModal
        open={aiStudioOpen}
        onClose={() => setAiStudioOpen(false)}
      />

      {/* Filmhook Editor Interactive 3D LUT Color Grading Sandbox Modal */}
      <FilmhookColorLUTSandboxModal
        open={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
      />

      {/* Global QR & Send-to-Mobile Scanner Modal */}
      <AppQRScannerModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        defaultAppId={qrTargetId}
        initialTab={qrInitialTab}
      />
    </Box>
  );
};

export default ProductShowcaseSection;
 